import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, EMPTY, Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import {
  ConfigService,
  isPresent,
  NativeBridgeService,
  pathSegmentsToPathString,
  RootPaths,
  SentryLogger,
  unsubscribe,
} from '@fcom/core/index';
import {
  CheckInAcceptCancelRequest,
  FinnairBoundItem,
  FinnairOrder,
  FinnairPassengerItem,
} from '@fcom/dapi/api/models';
import { BoundType, DapiErrorCode, DapiHttpErrorResponse } from '@fcom/dapi/index';
import { GtmService } from '@fcom/common/gtm';
import { LanguageService } from '@fcom/ui-translate/index';
import { ToasterService } from '@fcom/common/services';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import {
  ElementActions,
  ElementTypes,
  GaContext,
  GtmPurchaseFlow,
  LoaderType,
  ToasterMessage,
} from '@fcom/common/interfaces';
import {
  CommonBookingFeatureState,
  OrderActions,
  OrderStatus,
  paymentIdsPaid,
  PaymentStatus,
  paymentStatus,
} from '@fcom/common-booking/index';
import { NotificationTheme } from '@fcom/ui-components/index';
import { MmbOrderService } from '@fcom/manage-booking-common/index';
import { mapPaymentMethods } from '@fcom/common-booking/utils/payment.utils';
import { setPaymentIdPaid } from '@fcom/core/actions/global.actions';
import { finShare } from '@fcom/rx/operators/finShare';

import { ManageBookingFeatureState } from '../../../interfaces';
import { CheckInService } from '../../../services/check-in.service';

interface AcceptanceOrderResponse {
  order: FinnairOrder;
  acceptance: {
    success: boolean;
    reason?: string;
  };
}

@Component({
  selector: 'fin-check-in-rules-route',
  templateUrl: 'check-in-rules-route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInRulesRouteComponent implements OnInit, OnDestroy {
  readonly OrderStatus = OrderStatus;
  readonly LoaderType = LoaderType;
  readonly GaContext = GaContext;
  readonly BoundType = BoundType;

  order$: Observable<FinnairOrder>;
  orderStatus$: Observable<OrderStatus>;
  bound$: Observable<FinnairBoundItem>;
  selectedForCheckInPassengers$: Observable<FinnairPassengerItem[]>;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  checkoutUrl: string;

  private subscriptions = new Subscription();

  constructor(
    private store$: Store<CommonBookingFeatureState & ManageBookingFeatureState>,
    private gtmService: GtmService,
    private mmbOrderService: MmbOrderService,
    private toasterService: ToasterService,
    private languageService: LanguageService,
    private router: Router,
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private locationService: Location,
    private checkInService: CheckInService,
    private sentryLogger: SentryLogger,
    private nativeBridgeService: NativeBridgeService
  ) {}

  ngOnInit(): void {
    this.checkoutUrl = this.configService.cfg.checkoutUrl;

    this.order$ = this.checkInService.getOrder();
    this.orderStatus$ = this.checkInService.getOrderStatus();
    this.bound$ = this.checkInService.getBound();
    this.selectedForCheckInPassengers$ = this.checkInService.getPassengers();

    this.subscriptions.add(
      this.activatedRoute.queryParams
        .pipe(
          take(1),
          filter((queryParams) => queryParams?.orderid && queryParams?.hash),
          switchMap((queryParams) => {
            const { orderid, hash } = queryParams;

            return this.mmbOrderService.getOrderByIdAndHash(orderid, hash).pipe(
              catchError((error) => {
                this.sentryLogger.error('Error fetching order', { error });
                return EMPTY;
              }),
              switchMap((order) =>
                this.activatedRoute.data.pipe(
                  take(1),
                  map((data) => ({
                    order,
                    isPaymentSuccess: isPresent(data?.isPaymentSuccess),
                  }))
                )
              )
            );
          })
        )
        .subscribe({
          next: ({ order, isPaymentSuccess }) => {
            this.store$.dispatch(OrderActions.setOrderData({ order }));
            this.locationService.replaceState(pathSegmentsToPathString(this.activatedRoute.snapshot.pathFromRoot));

            if (isPaymentSuccess) {
              const message: ToasterMessage = {
                id: 'checkInPaymentResult',
                theme: NotificationTheme.SUCCESS,
                contentKey: 'MMB.checkIn.payment.success',
              };
              this.toasterService.addMessageToQueue(message);
            }
          },
        })
    );

    this.subscriptions.add(
      this.store$
        .pipe(
          paymentStatus(),
          filter((statusOfPayment) => statusOfPayment === PaymentStatus.VERIFIED),
          withLatestFrom(this.order$.pipe(map(mapPaymentMethods)), this.order$, this.store$.pipe(paymentIdsPaid())),
          filter(([_, __, order, paidPaymentIds]) => {
            const paymentId = order.checkouts?.sort((a, b) => +a.timestamp - +b.timestamp)[0]?.id;
            return !paidPaymentIds.some((id) => id === paymentId);
          })
        )
        .subscribe(([_, paymentMethods, order]) => {
          const paymentId = order.checkouts?.sort((a, b) => +a.timestamp - +b.timestamp)[0]?.id;
          const totalAmount =
            order.prices?.checkouts?.find((c) => c.id === paymentId)?.prices?.services?.total?.totalAmount?.amount ??
            order.prices?.pending?.services?.total?.totalAmount.amount;
          const containsPricesToReport = parseFloat(totalAmount) > 0;
          if (containsPricesToReport) {
            this.gtmService.completeManageBookingPurchase(order, paymentMethods, GtmPurchaseFlow.MANAGE_BOOKING);
            this.store$.dispatch(setPaymentIdPaid({ paymentId }));
          }
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  completeCheckIn(): void {
    this.subscriptions.add(
      combineLatest([this.order$, this.selectedForCheckInPassengers$, this.bound$])
        .pipe(
          take(1),
          tap(() => {
            this.gtmService.trackElement(
              'check-in-flow_confirm-cta_check-in',
              GaContext.CHECK_IN,
              ElementTypes.BUTTON,
              undefined,
              ElementActions.CLICK
            );
            this.loading$.next(true);
          }),
          switchMap(([order, passengers, bound]) => {
            const request: CheckInAcceptCancelRequest = {
              flightIds: bound.itinerary.filter(isFlight).map((f) => f.id),
              travelerIds: passengers.map(({ id }) => id),
            };
            return this.mmbOrderService.completeCheckIn(order.id, order.hash, request).pipe(
              map(
                (orderData) =>
                  ({
                    order: orderData,
                    acceptance: {
                      success: true,
                    },
                  }) as AcceptanceOrderResponse
              ),
              catchError((error: unknown) => {
                if (
                  error instanceof DapiHttpErrorResponse &&
                  (error.error?.key === DapiErrorCode.CHECK_IN_ACCEPT_UNRECOVERABLE_FAILURE ||
                    error.error?.key === DapiErrorCode.CHECK_IN_ACCEPT_FAILED)
                ) {
                  return this.mmbOrderService.getOrderByIdAndHash(order.id, order.hash).pipe(
                    take(1),
                    map(
                      (order) =>
                        ({
                          order,
                          acceptance: {
                            success: false,
                            reason: error.error?.key,
                          },
                        }) as AcceptanceOrderResponse
                    )
                  );
                }
                return throwError(() => error);
              }),
              map((response) => ({ response, request })),
              catchError((error) => {
                return throwError(() => ({
                  error,
                  request,
                }));
              })
            );
          }),
          finShare()
        )
        .subscribe({
          next: ({ response, request }) => {
            const { acceptance, order } = response;
            if (this.nativeBridgeService.isInsideNativeWebView) {
              this.nativeBridgeService.onAcceptCheckIn({
                status: true,
                flightIds: request.flightIds,
                travelerIds: request.travelerIds,
              });
            }
            this.store$.dispatch(OrderActions.setOrderData({ order }));
            if (acceptance.success) {
              this.checkInService.triggerNextCheckInStep();
            } else {
              this.router.navigateByUrl(`${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/view`);
              this.toasterService.addMessageToQueue({
                id: 'checkIn',
                theme: NotificationTheme.ALERT,
                iconName: SvgLibraryIcon.ALERT_TRIANGLE,
                keepMessageAfterNavigation: false,
                delay: true,
                contentKey:
                  acceptance.reason === DapiErrorCode.CHECK_IN_ACCEPT_UNRECOVERABLE_FAILURE
                    ? 'MMB.checkIn.confirmation.notifications.nonRecoverableError'
                    : 'MMB.checkIn.confirmation.notifications.error',
              });
            }
            this.loading$.next(false);
          },
          error: ({ error, request }: { error: unknown; request: CheckInAcceptCancelRequest }) => {
            if (this.nativeBridgeService.isInsideNativeWebView) {
              this.nativeBridgeService.onAcceptCheckIn({
                status: false,
                flightIds: request.flightIds,
                travelerIds: request.travelerIds,
              });
            }
            this.sentryLogger.error('Unexpected error in completeCheckIn', { error });
            this.router.navigateByUrl(`${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/view`);
            this.loading$.next(false);
          },
        })
    );
  }
}
